import styled from 'styled-components'

import { RoleTone, theme } from '../../constants'

const roleBackground = (role: RoleTone) => theme.roleColors[role].main

export const Frame = styled.div<{ $role: RoleTone }>`
  width: 100%;
  height: 100vh;
  display: flex;
  overflow: hidden;
  background: ${({ $role }) => roleBackground($role)};
  font-family: ${theme.typography.family};
  text-align: left;

  * {
    box-sizing: border-box;
  }
`

export const Sidebar = styled.aside<{
  $open: boolean
  $role: RoleTone
}>`
  position: relative;
  z-index: 30;
  width: ${({ $open }) =>
    $open
      ? theme.layout.sidebarWidthExpanded
      : theme.layout.sidebarWidthCollapsed};
  height: 100vh;
  flex: 0 0 auto;
  overflow: hidden;
  color: ${({ $role }) => theme.roleColors[$role].text};
  background: ${({ $role }) => roleBackground($role)};
  transition: width ${theme.motion.normal} ${theme.motion.easing};

  @media (max-width: ${theme.breakpoints.desktop}) and (min-width: calc(${theme
      .breakpoints.mobile} + 1px)) {
    width: ${({ $open }) =>
      $open ? theme.layout.sidebarWidthExpanded : '80px'};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    position: fixed;
    inset: 0 auto 0 0;
    width: 264px;
    box-shadow: ${({ $open }) =>
      $open ? `12px 0 32px ${theme.effects.shadowOverlay}` : 'none'};
    transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition:
      transform ${theme.motion.normal} ${theme.motion.easing},
      box-shadow ${theme.motion.normal} ${theme.motion.easing};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const Navigation = styled.nav`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing[6]} ${theme.spacing[3]} ${theme.spacing[4]};
  box-sizing: border-box;
`

export const MenuToggle = styled.button<{ $open: boolean }>`
  position: fixed;
  top: 22px;
  left: ${({ $open }) => ($open ? '204px' : '22px')};
  z-index: 40;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  padding: 0;
  color: ${theme.colors.black};
  background: ${theme.effects.surfaceOverlay};
  border: 1px solid ${theme.effects.borderOverlay};
  border-radius: ${theme.radii.medium};
  font-size: 22px;
  cursor: pointer;
  backdrop-filter: blur(8px);

  &:hover {
    background: ${theme.effects.surfaceHover};
  }

  &:focus-visible {
    outline: 3px solid ${theme.effects.focusOverlay};
    outline-offset: 2px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 24px;
    left: ${({ $open }) => ($open ? '204px' : '24px')};
    background: ${theme.colors.white};
    box-shadow: ${theme.shadows.medium};
    transition: left ${theme.motion.normal} ${theme.motion.easing};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 33px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const Logo = styled.div<{ $open: boolean }>`
  width: 100%;
  height: 48px;
  margin-bottom: ${theme.spacing[12]};
  padding-right: 48px;
  overflow: hidden;
  box-sizing: border-box;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};

  a {
    height: 48px;
    display: flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    color: inherit;
    text-decoration: none;
    white-space: nowrap;
  }

  img {
    width: 40px;
    height: 40px;
    flex: 0 0 auto;
  }

  strong {
    font-size: 24px;
    letter-spacing: -0.04em;
  }
`

export const MenuItems = styled.div<{ $open: boolean }>`
  min-height: 0;
  display: flex;
  align-items: ${({ $open }) => ($open ? 'stretch' : 'center')};
  flex: 1;
  flex-direction: column;
  gap: 10px;

  .logout {
    margin-top: auto;
  }
`

export const MenuItem = styled.button<{ $active?: boolean; $open: boolean }>`
  width: ${({ $open }) => ($open ? '100%' : '48px')};
  height: 48px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: ${({ $open }) => ($open ? 'flex-start' : 'center')};
  gap: ${({ $open }) => ($open ? '14px' : '0')};
  padding: ${({ $open }) => ($open ? '11px 14px' : '0')};
  overflow: hidden;
  color: inherit;
  background: ${({ $active }) =>
    $active ? theme.effects.surfaceActive : 'transparent'};
  border: 1px solid transparent;
  border-radius: ${theme.radii.medium};
  box-shadow: ${({ $active }) =>
    $active ? theme.shadows.small : theme.shadows.none};
  font-family: inherit;
  font-size: ${theme.typography.size.bodySmall};
  font-weight: ${theme.typography.weight.semibold};
  text-align: left;
  white-space: nowrap;
  cursor: pointer;

  svg {
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
  }

  &:hover {
    background: ${theme.effects.surfaceSoft};
  }

  &:focus-visible {
    outline: 3px solid ${theme.effects.focusLight};
    outline-offset: 1px;
  }
`

export const MainSurface = styled.main<{ $role: RoleTone }>`
  min-width: 0;
  height: calc(100vh - 24px);
  display: flex;
  flex: 1;
  flex-direction: column;
  margin: 12px 12px 12px 0;
  overflow-x: hidden;
  overflow-y: auto;
  color: ${theme.colors.textPrimary};
  background: ${theme.colors.background};
  border: 1px solid ${theme.effects.borderLight};
  border-radius: ${theme.radii.xlarge};
  scrollbar-color: ${({ $role }) => roleBackground($role)} transparent;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ $role }) => roleBackground($role)};
    border: 2px solid ${theme.colors.background};
    border-radius: ${theme.radii.pill};
  }

  @media (max-width: ${theme.breakpoints.desktop}) {
    width: calc(100% - 24px);
    height: calc(100vh - 24px);
    margin: 12px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: calc(100% - 12px);
    height: calc(100vh - 12px);
    margin: 6px;
    border-radius: ${theme.radii.large};

    &::-webkit-scrollbar {
      width: 5px;
    }
  }
`

export const Header = styled.header`
  position: relative;
  min-height: 112px;
  padding: ${theme.spacing[6]} ${theme.layout.contentGutter};
  background: ${theme.colors.background};
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 216px;
    padding-top: 104px;
    padding-bottom: ${theme.spacing[6]};
  }
`

export const HeaderInner = styled.div`
  width: 100%;
  max-width: ${theme.layout.contentMaxWidth};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.mobile}) {
    align-items: stretch;
    flex-direction: column;
  }
`

export const HeaderCopy = styled.div`
  min-width: 0;

  > span {
    display: block;
    margin-bottom: ${theme.spacing[1]};
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  h1 {
    margin: 0;
    font-size: ${theme.typography.size.h1};
    line-height: ${theme.typography.lineHeight.heading};
    letter-spacing: -0.035em;
  }

  p {
    margin: ${theme.spacing[1]} 0 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.size.bodySmall};
    line-height: ${theme.typography.lineHeight.body};
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    h1 {
      font-size: 26px;
    }
  }
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.mobile}) {
    position: absolute;
    top: 24px;
    right: ${theme.spacing[4]};
    width: auto;
    align-self: auto;
    justify-content: flex-end;
    padding-right: 0;
    order: 0;
  }
`

export const RoundButton = styled.button<{ $role?: RoleTone }>`
  position: relative;
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  padding: 0;
  color: ${theme.semanticColors.text.secondary};
  background: ${theme.semanticColors.background.elevated};
  border: 1px solid ${theme.semanticColors.border.strong};
  border-radius: ${theme.radii.round};
  font-size: 20px;
  cursor: pointer;

  &:hover {
    color: ${theme.semanticColors.text.primary};
    border-color: ${({ $role = 'student' }) => theme.roleColors[$role].main};
    background-color: ${theme.colors.backgroundGrey};
  }

  &:focus-visible {
    outline: 3px solid
      ${({ $role = 'student' }) => theme.roleColors[$role].focus};
    outline-offset: 2px;
  }
`

export const ProfileButton = styled(RoundButton)<{
  $image: string
  $role?: RoleTone
}>`
  background-image: url(${({ $image }) => $image});
  background-position: center;
  background-size: cover;
  border: 1px solid ${theme.semanticColors.border.strong};
  box-shadow: none;
`

export const PopoverAnchor = styled.div`
  position: relative;
`

export const ProfileMenuPanel = styled.div<{ $role?: RoleTone }>`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  z-index: 100;
  width: 210px;
  padding: ${theme.spacing[2]};
  background: ${theme.semanticColors.background.surface};
  border: 1px solid ${theme.semanticColors.border.default};
  border-radius: ${theme.radii.large};
  box-shadow: ${theme.shadows.large};

  > button {
    width: 100%;
    min-height: 44px;
    display: flex;
    align-items: center;
    gap: ${theme.spacing[2]};
    padding: ${theme.spacing[2]} ${theme.spacing[3]};
    color: ${theme.semanticColors.text.primary};
    background: transparent;
    border: 0;
    border-radius: ${theme.radii.small};
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  > button:hover {
    color: ${({ $role = 'student' }) => theme.roleColors[$role].text};
    background: ${({ $role = 'student' }) => theme.roleColors[$role].main};
  }

  > button:focus-visible {
    color: ${({ $role = 'student' }) => theme.roleColors[$role].text};
    background: ${({ $role = 'student' }) => theme.roleColors[$role].main};
    outline: 3px solid ${theme.semanticColors.focus};
    outline-offset: 1px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    position: fixed;
    top: 84px;
    right: ${theme.spacing[4]};
    width: min(410px, calc(100vw - 32px));
    max-height: calc(100dvh - 108px);
    overflow-y: auto;
  }
`

export const ThemeSelectorGroup = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[2]} ${theme.spacing[3]};
  border-bottom: 1px solid ${theme.colors.border};

  > span {
    display: block;
    margin-bottom: ${theme.spacing[2]};
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
    font-weight: ${theme.typography.weight.bold};
  }
`

export const ThemeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[1]};
`

export const ThemeOption = styled.button<{
  $selected: boolean
  $role?: RoleTone
}>`
  min-width: 0;
  min-height: 44px;
  justify-content: center;
  flex-direction: column;
  gap: 2px !important;
  padding: ${theme.spacing[1]} !important;
  color: ${({ $selected }) =>
    $selected
      ? 'var(--theme-option-role-text)'
      : theme.colors.textPrimary} !important;
  background: ${({ $selected }) =>
    $selected ? 'var(--theme-option-role-main)' : 'transparent'} !important;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--theme-option-role-main)' : 'transparent'} !important;
  font-size: ${theme.typography.size.caption} !important;
  text-align: center !important;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: ${({ $selected }) =>
      $selected
        ? 'var(--theme-option-role-text)'
        : theme.colors.textPrimary} !important;
    background: ${({ $selected }) =>
      $selected
        ? 'var(--theme-option-role-main)'
        : theme.colors.backgroundGrey} !important;
  }
`

export const Badge = styled.span<{ $role?: RoleTone }>`
  position: absolute;
  top: -9px;
  right: -9px;
  z-index: 2;
  min-width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  padding: 0 5px;
  color: ${({ $role = 'student' }) => theme.roleColors[$role].text};
  background: ${({ $role = 'student' }) => theme.roleColors[$role].main};
  border: 2px solid ${theme.colors.background};
  border-radius: ${theme.radii.pill};
  box-sizing: border-box;
  font-size: ${theme.typography.size.caption};
  font-weight: ${theme.typography.weight.bold};
`

export const NotificationPanel = styled.div<{ $role?: RoleTone }>`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  z-index: 100;
  width: min(390px, calc(100vw - 32px));
  max-height: 520px;
  overflow: hidden;
  background: ${theme.semanticColors.background.elevated};
  border: 1px solid ${theme.semanticColors.border.default};
  border-radius: ${theme.radii.xlarge};
  box-shadow: ${theme.shadows.large};

  &::before {
    content: '';
    display: block;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${({ $role = 'student' }) => theme.roleColors[$role].main},
      ${theme.colors.brandCoral}
    );
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    position: fixed;
    top: 84px;
    right: ${theme.spacing[4]};
    left: auto;
    width: min(410px, calc(100vw - 32px));
    max-width: none;
    max-height: calc(100dvh - 108px);
    border-radius: ${theme.radii.large};
  }
`

export const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.semanticColors.border.default};

  ${RoundButton} {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
`

export const NotificationHeaderCopy = styled.div`
  min-width: 0;

  h2 {
    margin: 0;
    color: ${theme.semanticColors.text.primary};
    font-size: ${theme.typography.size.body};
    font-weight: ${theme.typography.weight.bold};
    letter-spacing: -0.02em;
  }

  span {
    display: block;
    margin-top: 5px;
    color: ${theme.semanticColors.text.muted};
    font-size: ${theme.typography.size.caption};
  }
`

export const NotificationList = styled.div`
  max-height: 390px;
  padding: ${theme.spacing[3]};
  overflow-y: auto;

  @media (max-width: ${theme.breakpoints.mobile}) {
    max-height: calc(100dvh - 180px);
  }
`

export const NotificationItem = styled.button<{
  $unread: boolean
  $role?: RoleTone
}>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  min-height: 72px;
  padding: ${theme.spacing[3]} ${theme.spacing[2]};
  color: ${({ $unread }) =>
    $unread ? 'var(--notification-role-text)' : theme.colors.textPrimary};
  background: ${({ $unread }) =>
    $unread ? 'var(--notification-role-soft)' : 'transparent'};
  border: 1px solid
    ${({ $unread }) =>
      $unread ? 'var(--notification-role-main)' : 'transparent'};
  border-radius: ${theme.radii.medium};
  font: inherit;
  text-align: left;
  cursor: pointer;

  & + & {
    margin-top: ${theme.spacing[2]};
  }

  &:hover {
    background: ${theme.colors.backgroundGrey};
  }

  &:focus-visible {
    outline: 3px solid var(--notification-role-focus);
  }

  > span {
    width: 58px;
    height: 58px;
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    color: var(--notification-role-strong);
    background: var(--notification-role-soft);
    border-radius: ${theme.radii.round};

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .content {
    min-width: 0;
    flex: 1;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: ${theme.spacing[2]};
  }

  strong {
    min-width: 0;
    overflow: hidden;
    font-size: ${theme.typography.size.bodySmall};
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  time,
  p {
    color: ${({ $unread }) =>
      $unread
        ? 'color-mix(in srgb, var(--notification-role-text) 68%, transparent)'
        : theme.colors.textMuted};
    font-size: ${theme.typography.size.caption};
  }

  p {
    margin: ${theme.spacing[1]} 0 0;
    line-height: ${theme.typography.lineHeight.compact};
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 76px;
    padding: ${theme.spacing[3]} ${theme.spacing[2]};
  }
`

export const EmptyNotifications = styled.p`
  margin: 0;
  padding: ${theme.spacing[6]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.size.bodySmall};
  text-align: center;
`
